/**
 * TicketTransactionsCard — latest platform-wide ticket transactions on the
 * admin dashboard, linking to the full /admin/transactions ledger.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, Receipt, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOURCE_BADGE = {
  widget: { label: 'WIDGET', cls: 'bg-indigo-500/20 text-indigo-300 border-indigo-500/30' },
  venue_experiences: { label: 'VENUE EXP', cls: 'bg-amber-500/20 text-amber-300 border-amber-500/30' },
  missed_upgrade_email: { label: 'UPGRADE', cls: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' },
};
const STATUS_CLS = {
  completed: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  pending: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  refunded: 'bg-red-500/15 text-red-400 border-red-500/30',
};

export default function TicketTransactionsCard({ token }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/transactions?limit=8`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  return (
    <Card className="bg-card border-border" data-testid="ticket-transactions-card">
      <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
        <CardTitle className="text-lg flex items-center gap-2">
          <Receipt className="h-5 w-5 text-emerald-400" /> Ticket Transactions
          {data && <span className="text-xs font-normal text-muted-foreground">({data.total.toLocaleString()} total)</span>}
        </CardTitle>
        <Button size="sm" variant="outline" onClick={() => navigate('/admin/transactions')} data-testid="view-all-transactions-btn">
          View all <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-indigo-400" /></div>
        ) : !data || data.transactions.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">No ticket transactions yet.</p>
        ) : (
          <div>
            {data.transactions.map(t => {
              const src = SOURCE_BADGE[t.source];
              return (
                <button key={t.order_id}
                  onClick={() => navigate(`/admin/events/${t.event_id}/transactions`)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 border-b border-border last:border-0 hover:bg-accent/50 transition-colors text-left"
                  data-testid={`txn-row-${t.order_id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{t.buyer_name || t.buyer_email}</p>
                    <p className="text-[11px] text-muted-foreground truncate">{t.event_name} · {t.tickets}t{t.addons > 0 ? ` + ${t.addons} add-on${t.addons > 1 ? 's' : ''}` : ''}</p>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {src && <Badge className={`${src.cls} border text-[8px] px-1`}>{src.label}</Badge>}
                    <Badge className={`${STATUS_CLS[t.status] || 'bg-muted text-muted-foreground'} border text-[8px] px-1 uppercase`}>{t.status}</Badge>
                  </div>
                  <div className="text-right flex-shrink-0 w-20">
                    <p className="text-sm font-bold">{t.total === 0 ? 'Free' : `$${t.total.toFixed(2)}`}</p>
                    {t.fee_cents > 0 && <p className="text-[10px] text-amber-400">fee ${(t.fee_cents / 100).toFixed(2)}</p>}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
